<template>
  <div class="floricultura-page">
    <Header />

    <div class="container">
      <div class="page-header">
        <h1 class="page-title">Floricultura</h1>
        <p class="page-subtitle">Coroa de Flores, Buquês, Vasos e Arranjos</p>
      </div>

      <CatalogSearchBar
        :value="busca"
        placeholder="Buscar produtos..."
        @input="busca = $event"
      />

      <CategoryFilter
        :categorias="categoriasFloricultura"
        :categoria-selecionada="categoriaSelecionada"
        @filter-change="onCategoriaChange"
        :show-todos="true"
      />

      <div v-if="carregando" class="loading">
        <div class="spinner"></div>
        <p>Carregando produtos...</p>
      </div>

      <div v-else-if="erro" class="error-message">
        <p>{{ erro }}</p>
        <button @click="carregarDados" class="btn-retry">
          Tentar novamente
        </button>
      </div>

      <div v-else-if="produtosFiltrados.length === 0" class="empty-state">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <p>Nenhum produto encontrado</p>
      </div>

      <div v-else>
        <div class="products-grid">
          <ProductCard
            v-for="produto in produtosPaginados"
            :key="produto.id"
            :produto="produto"
            :categorias="categorias"
          />
        </div>
        <div v-if="totalPaginas > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="paginaAtual <= 1"
            @click="irParaPagina(paginaAtual - 1)"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 19L8 12L15 5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <span class="pagination-info">
            Página {{ paginaAtual }} de {{ totalPaginas }}
          </span>
          <button
            class="pagination-btn"
            :disabled="paginaAtual >= totalPaginas"
            @click="irParaPagina(paginaAtual + 1)"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5L16 12L9 19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Header from "../components/Header.vue";
import Footer from "../components/Footer.vue";
import ProductCard from "../components/ProductCard.vue";
import CatalogSearchBar from "../components/CatalogSearchBar.vue";
import CategoryFilter from "../components/CategoryFilter.vue";
import produtoService from "../services/produtoService";
import categoriaService from "../services/categoriaService";

export default {
  name: "Floricultura",
  components: {
    Header,
    Footer,
    ProductCard,
    CatalogSearchBar,
    CategoryFilter,
  },
  data() {
    return {
      produtos: [],
      categorias: [],
      categoriaSelecionada: null,
      busca: "",
      carregando: false,
      erro: null,
      paginaAtual: 1,
      itensPorPagina: 9,
    };
  },
  computed: {
    categoriasFloricultura() {
      const nomesFloricultura = [
        "coroa",
        "coroas",
        "buque",
        "buquê",
        "buques",
        "buquês",
        "vaso",
        "vasos",
        "arranjo",
        "arranjos",
      ];

      return this.categorias.filter((categoria) => {
        const nomeLower = categoria.descricao.toLowerCase();
        return nomesFloricultura.some((nome) => nomeLower.includes(nome));
      });
    },
    produtosFiltrados() {
      let filtrados = this.produtos;

      const idsCategoriasFloricultura = this.categoriasFloricultura.map(
        (c) => c.id,
      );
      filtrados = filtrados.filter((p) =>
        idsCategoriasFloricultura.includes(p.categoriaId),
      );

      if (this.categoriaSelecionada) {
        filtrados = filtrados.filter(
          (p) => p.categoriaId === this.categoriaSelecionada,
        );
      }

      if (this.busca) {
        const buscaLower = this.busca.toLowerCase();
        filtrados = filtrados.filter(
          (p) =>
            p.descricao.toLowerCase().includes(buscaLower) ||
            p.nome.toLowerCase().includes(buscaLower) ||
            p.tipo.toLowerCase().includes(buscaLower) ||
            p.fornecedor.toLowerCase().includes(buscaLower),
        );
      }

      return filtrados;
    },
    totalPaginas() {
      return (
        Math.ceil(this.produtosFiltrados.length / this.itensPorPagina) || 1
      );
    },
    produtosPaginados() {
      const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
      return this.produtosFiltrados.slice(inicio, inicio + this.itensPorPagina);
    },
  },
  watch: {
    "$route.query.categoria"() {
      this.categoriaInicialDaQuery();
    },
    categoriaSelecionada() {
      this.paginaAtual = 1;
    },
    busca() {
      this.paginaAtual = 1;
    },
    totalPaginas(novoTotal) {
      if (this.paginaAtual > novoTotal && novoTotal > 0) {
        this.paginaAtual = novoTotal;
      }
    },
  },
  mounted() {
    this.carregarDados();
  },
  methods: {
    async carregarDados() {
      this.carregando = true;
      this.erro = null;

      try {
        this.categorias = await categoriaService.listarCategorias();
        this.produtos = await produtoService.listarProdutos();
        this.categoriaInicialDaQuery();
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        this.erro =
          "Erro ao carregar produtos. Verifique sua conexão e tente novamente.";
      } finally {
        this.carregando = false;
      }
    },
    categoriaInicialDaQuery() {
      const slug = this.$route.query.categoria;
      if (!this.categoriasFloricultura.length) return;
      if (!slug) {
        this.categoriaSelecionada = null;
        return;
      }
      const slugNorm = String(slug)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      const cat = this.categoriasFloricultura.find((c) => {
        const d = (c.descricao || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        return d.includes(slugNorm) || slugNorm.includes(d);
      });
      this.categoriaSelecionada = cat ? cat.id : null;
    },
    irParaPagina(numero) {
      this.paginaAtual = numero;
      this.$el
        .querySelector(".products-grid")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    onCategoriaChange(categoriaId) {
      this.categoriaSelecionada = categoriaId;
    },
  },
};
</script>

<style scoped>
.floricultura-page {
  min-height: 100vh;
  background: #ffffff;
}

.container {
  max-width: 1320px;
  margin: 0 auto;
  padding: 40px;
}

.page-header {
  margin-bottom: 40px;
}

.page-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.875rem, 4vw, 3rem);
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #718096;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: #34894d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  text-align: center;
  padding: 80px 20px;
  color: #f56565;
}

.btn-retry {
  margin-top: 20px;
  padding: 12px 24px;
  background: #34894d;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #2d7a42;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #a0aec0;
}

.empty-state svg {
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state p {
  font-size: 1.125rem;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1.5rem 0;
}

.pagination-btn {
  padding: 0.4rem 0.6rem;
  background: #34894d;
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background: #2d7a42;
}

.pagination-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

@media (min-width: 640px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .container {
    padding: 24px 20px;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }
}
</style>
